/**
 * A precompile pass that actually covers the whole scene — and reports itself
 * while it does.
 *
 * `WebGPURenderer.compileAsync(scene, camera)` sounds like "compile every
 * material in this scene", and its own doc comment says so, but its render
 * list is built by `Renderer._projectObject` — the same walk `render()` uses,
 * with the same two exits: `object.visible === false` returns immediately, and
 * a mesh with `frustumCulled` left at its default is skipped unless it
 * intersects *this camera's frustum right now*. Anything behind you at the
 * moment you call it is therefore not precompiled at all.
 *
 * That is invisible in most three.js scenes, because a pipeline is cached per
 * *material*: the one object of a given material that happens to face you pays
 * the compile and every sibling reuses it. It is not invisible here.
 * `RenderObject.getMaterialCacheKey()` in three r185 appends `object.uuid` for
 * any `InstancedMesh` (an upstream `TODO`, and the subject of the 2026-08-19
 * world-stability entry in `lessons.md`), so a region's ~130 instanced meshes
 * are ~130 separate pipelines even though they share two materials.
 * Precompiling only the ones in front of the aeroplane leaves the rest to
 * compile synchronously, mid-flight, on the frame each first enters the
 * frustum — which is to say: on a turn (measured at 2–27 ms per such frame
 * against a 0.8 ms median, perf lane, 2026-08-23).
 *
 * The fix is to lift both exits for the duration of the compile and put them
 * back afterwards. Nothing is drawn — `compileAsync` builds pipelines and
 * never submits a pass — so forcing `visible` costs a picture nothing, and it
 * is what reaches the sprite pools, which sit at `visible = false` until their
 * first puff (`three/screen-sprites.ts`).
 *
 * ## Why this is a loop over chunks, and why the loop is now concurrent
 *
 * **The chunking was for the loading screen; the concurrency is for the
 * clock.** `compileAsync` in three r185 is a serial loop of its own:
 * `Renderer.compileAsync` collects the render list's work items and walks them
 * one at a time, `await`ing each object's pipeline before starting the next,
 * under the upstream comment "Process compilation work items sequentially to
 * avoid freezing". Censused on a real Blackwell adapter that is exactly what
 * happens — one pipeline in flight at any instant, each
 * `createRenderPipelineAsync` round trip ~6 ms, the adapter idle between, and
 * the phase lasting the sum (`docs/engineering/fly-boot-timing.md` §6e).
 *
 * So the phase costs **pipeline count × round trip**, and it used to be one
 * opaque `await` that could say nothing for however long it ran. On this
 * machine that is under a second; on a machine whose round trip is fifty times
 * slower it is the minute the user reported, with a full bar and a ticking
 * second-counter throughout.
 *
 * Splitting the scene into subtrees lets the boot report a real fraction
 * between chunks. `compileAsync(object, camera, targetScene)` is three's own
 * documented way to compile part of a scene: it builds its render list against
 * `targetScene`, so a per-chunk call produces the *same* material cache keys —
 * and therefore the same pipelines — as one whole-scene call. That equality is
 * this module's invariant and it is asserted by census rather than assumed.
 *
 * **Issuing the chunks concurrently is then most of the phase back, and it was
 * held back for a whole wave because the only instrument available could not
 * see the risk.** Two `compileAsync` loops in flight interleave
 * `_nodes.updateBefore` / `updateForRender` / `updateAfter` on a shared node
 * frame, which is a wrong-*uniform* risk — a picture defect that a pipeline
 * census is constitutionally unable to detect, and this repo has a standing
 * entry about fixes that were adjusting the appearance of geometry that never
 * reached the framebuffer. The instrument that settled it is
 * `apps/web/scripts/pose-sweep.ts`: eight camera poses across five sorties on
 * the real adapter, frozen before the sim's first tick, with a
 * same-configuration control column measuring the rig's own noise. **26 of 40
 * poses byte-identical across the change; the 14 that move are the ones
 * holding the propeller disc, and they move by the same amount when nothing
 * changes at all.** Same result on the WebGL2 backend. §6f is the table.
 *
 * What it buys, same rig, Verdun: `precompile` **680 → 355 ms** and the boot
 * **1,952 → 1,620 ms** on WebGPU, and **1,751 → 477 ms** / **2,965 → 1,640 ms**
 * on the WebGL2 fallback, which is the backend a machine without WebGPU is
 * already on and the one where this phase was worst.
 *
 * The chunking's own price stands as measured: ~70 ms spread over ~147
 * `compileAsync` calls where there was one, 4.7 % of a healthy boot. The lever
 * that would recover most of it — batching sibling chunks behind one proxy
 * `Object3D` so the call count falls to ~25 — is priced but not built, because
 * it needs a container object the scene graph does not otherwise have.
 */

import type * as THREE from "three/webgpu";

/** One object's saved flags, held only for the length of a precompile pass. */
interface SavedFlags {
  readonly object: THREE.Object3D;
  readonly visible: boolean;
  readonly frustumCulled: boolean;
}

/**
 * Clears `visible`/`frustumCulled` over the whole subtree and returns what to
 * hand `restoreSceneFlags`. Exported for its test; callers want
 * `precompileScene`.
 */
export function forceSceneFlags(root: THREE.Object3D): readonly SavedFlags[] {
  const saved: SavedFlags[] = [];
  root.traverse((object) => {
    if (object.visible && object.frustumCulled === false) return;
    saved.push({ object, visible: object.visible, frustumCulled: object.frustumCulled });
    object.visible = true;
    object.frustumCulled = false;
  });
  return saved;
}

/** Puts back exactly what `forceSceneFlags` took, in any order. */
export function restoreSceneFlags(saved: readonly SavedFlags[]): void {
  for (const entry of saved) {
    entry.object.visible = entry.visible;
    entry.object.frustumCulled = entry.frustumCulled;
  }
}

/**
 * The point below which the planner leaves a subtree alone.
 *
 * It is a *floor on splitting*, not a chunk size: descending is all-or-nothing
 * across a group's children (see `planPrecompileChunks`), so a group with more
 * drawables than this becomes one chunk **per child**, however small the
 * children are. On Verdun that is what happens to the region's instanced
 * scenery and to `terrain-decoration.ts`'s tiled trees, which are flat runs of
 * single-drawable siblings — so the constant barely moves the outcome, and the
 * measurement says so: **147 chunks over 175 drawables at 8, and 144 at 24**,
 * with the two boots inside each other's run-to-run spread.
 *
 * Left at 8 because the low value is the safe end of an inert knob: it is what
 * keeps a *small* scene — `/duel`'s, or a region that places almost nothing —
 * from being compiled as one silent call again.
 */
export const CHUNK_DRAWABLES = 8;

/**
 * The two properties the chunk planner reads, as a **self-referential
 * constraint** rather than a concrete interface.
 *
 * `THREE.Object3D` satisfies `ChunkCandidate<THREE.Object3D>` on its own terms —
 * its `children` really are `Object3D[]`, and `material` is optional and simply
 * absent on the ones that are not drawable — so the planner runs on the real
 * scene graph with no cast anywhere, and returns `Object3D`s that
 * `compileAsync` accepts directly. A test's own fake node type satisfies it the
 * same way, which is what keeps the plan assertable with no three.js, no GPU and
 * no DOM.
 *
 * `material` is how a drawable is told from a bare `Group`: `_projectObject`
 * pushes an object into the render list when it has one, and only those cost a
 * pipeline.
 */
export interface ChunkCandidate<T> {
  readonly children: readonly T[];
  readonly material?: unknown;
}

/** How many drawables `root`'s subtree holds, itself included. */
function drawableCount<T extends ChunkCandidate<T>>(root: T): number {
  let count = root.material === undefined ? 0 : 1;
  for (const child of root.children) count += drawableCount(child);
  return count;
}

/**
 * Splits `root` into subtrees of at most `maxDrawables` drawables each, whose
 * union is exactly `root`'s subtree — every drawable in exactly one chunk.
 *
 * **Coverage outranks resolution, and two rules keep it that way.** A subtree
 * is only replaced by its children when the root of it is *not itself*
 * drawable, because `compileAsync` has no way to compile one object without
 * its descendants: descending past a drawable root would silently drop that
 * root's own material, and a dropped material is a pipeline compiled later, on
 * the frame it first comes into view — the exact hitch this module exists to
 * prevent. And a childless subtree is returned whole whatever it counts,
 * because there is nothing to split it into.
 */
export function planPrecompileChunks<T extends ChunkCandidate<T>>(root: T, maxDrawables: number): readonly T[] {
  if (drawableCount(root) <= maxDrawables) return [root];
  if (root.material !== undefined || root.children.length === 0) return [root];
  const chunks: T[] = [];
  for (const child of root.children) chunks.push(...planPrecompileChunks(child, maxDrawables));
  return chunks;
}

/**
 * How far a precompile has got, reported once per chunk.
 *
 * `done` and `total` count **drawables**, not chunks, because chunks are
 * deliberately uneven — a subtree that cannot be split stays whole — and a
 * bar driven by chunk index would stall on the big ones and race through the
 * small ones. Drawables are the thing the phase's cost is linear in, which is
 * what makes them the honest denominator.
 */
export type PrecompileProgress = (done: number, total: number) => void;

/**
 * Whether the chunks are issued one at a time or all at once.
 *
 * `concurrent` is what ships. `serial` is three r185's own arrangement and is
 * kept as a **capture hatch**, on the same terms as `?renderer=webgl`: the
 * evidence that concurrency is safe is a pose-sweep pixel A/B on a real
 * adapter (`apps/web/scripts/pose-sweep.ts`), and an A/B needs both columns to
 * still exist. Nothing persists it and nothing links to it.
 */
export type PrecompileMode = "serial" | "concurrent" | "none";

/** The three the URL may name — `PrecompileMode` restated as data so the pin cannot drift from the type. */
const PRECOMPILE_MODES: readonly PrecompileMode[] = ["serial", "concurrent", "none"];

/**
 * `?precompile=serial|concurrent|none`, or nothing.
 *
 * `none` is not a configuration anybody should fly in — it is the control
 * column for "does the boot need all of this?", and what it measures is the
 * hitch the precompile exists to prevent (user reports 2026-08-21 and
 * 2026-08-23). Keeping it reachable is what stops that question from being
 * re-argued from first principles every time the boot gets long.
 */
export function precompilePin(search: string | undefined): PrecompileMode | undefined {
  if (!search) return undefined;
  const value = new URLSearchParams(search).get("precompile");
  return PRECOMPILE_MODES.find((mode) => mode === value);
}

/**
 * What a boot does when the URL says nothing.
 *
 * **Concurrent, and the reason is measured on both halves of the question.**
 * Speed: `precompile` **680 → 355 ms** and the boot **1,952 → 1,620 ms** on a
 * real Blackwell adapter, because three's loop leaves the adapter idle between
 * round trips and the phase is otherwise `pipelineCount × roundTrip`
 * (`docs/engineering/fly-boot-timing.md` §6e/§6f). Correctness: the worry was
 * that two `compileAsync` loops in flight interleave `_nodes.updateBefore` /
 * `updateAfter` on the shared node frame and leave a wrong uniform behind — a
 * picture defect no pipeline census can see. That was settled by photographing
 * the picture instead: §6f's pose sweep on the real adapter, against a
 * same-configuration control column that measures the rig's own noise.
 *
 * **`none` is not on this ladder and must not become the default by drift.**
 * It boots 450 ms faster here and needs only 64 of the boot's 108 pipelines in
 * ten seconds of hard turning, and on this adapter its frame-time distribution
 * is indistinguishable — median 0.60 ms, p99 ~19 ms, both columns. That is
 * precisely the trap: the machine that would benefit from deferring the
 * compile is the machine on which each deferred compile is a visible freeze,
 * because the quantity that makes its boot long is the per-pipeline cost. The
 * hitch is why this module exists (user reports 2026-08-21 and 2026-08-23).
 */
export const DEFAULT_PRECOMPILE_MODE: PrecompileMode = "concurrent";

/**
 * How many chunks `concurrent` keeps in flight — and it is a *bounded* pool
 * rather than `Promise.all` over the plan, for a reason the pixel A/B could not
 * have found.
 *
 * Issuing all ~147 chunks at once is the fastest arrangement and it takes the
 * loading bar back to the thing §6e existed to fix. Traced off the real
 * loading canvas at a 20× CPU throttle, sampled every 100 ms inside the page:
 *
 * | in flight | `precompile` (20× throttle) | samples in the phase | distinct bar widths | `precompile` (1×) |
 * |---|---|---|---|---|
 * | 1 (`serial`) | 10,046 ms | 90 | **31** | 680 ms |
 * | **8 (shipped)** | **7,645 ms** | 25 | **20** | **296–365 ms** |
 * | all at once | 4,434 ms | **8** | **3** | 346–401 ms |
 *
 * Eight samples in 4.4 seconds is a main thread blocked for half a second at a
 * stretch — three's own "to avoid freezing" comment, arriving as a measurement.
 * Unbounded made the phase 2.3× shorter and took the bar back to a jump.
 *
 * The pool gives the yield back and costs nothing where it matters: **on an
 * unthrottled boot the pool of eight is not slower than the unbounded one**,
 * because the idle time this change exists to remove is the *adapter's*, and
 * eight round trips saturate a driver's compile threads. What the unbounded
 * version bought was CPU parallelism the page needed for its own repaint.
 */
export const PRECOMPILE_IN_FLIGHT = 8;

/** The pin against the live `location`, read once — a document does not change its query mid-boot. */
let pinned: PrecompileMode | undefined;
let pinRead = false;

/**
 * What `precompileScene` uses when a caller names no mode: the URL's pin, or
 * the default.
 *
 * The one impure function in this module, and it is deliberately the *default
 * argument* rather than something the boot path has to remember to pass —
 * `scene-boot.ts` is on aislop's size ceiling and a hatch is not worth three
 * lines of it. Every test names its mode explicitly, so nothing under test ever
 * reaches a `location`.
 */
export function currentPrecompileMode(): PrecompileMode {
  if (!pinRead) {
    pinned = typeof location === "undefined" ? undefined : precompilePin(location.search);
    pinRead = true;
  }
  return pinned ?? DEFAULT_PRECOMPILE_MODE;
}

/**
 * Precompiles every pipeline the scene can ever need from `camera`, not merely
 * the ones currently on screen, reporting its progress as it goes.
 *
 * `finally` restores the flags even if a chunk rejects, so a failed precompile
 * costs a stutter and never a scene with everything switched on.
 */
export async function precompileScene(
  renderer: THREE.WebGPURenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  onProgress?: PrecompileProgress,
  mode: PrecompileMode = currentPrecompileMode(),
): Promise<void> {
  if (mode === "none") return;
  const saved = forceSceneFlags(scene);
  try {
    const chunks = planPrecompileChunks<THREE.Object3D>(scene, CHUNK_DRAWABLES);
    const weights = chunks.map(drawableCount);
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    // Reported before the first chunk, so the bar has a measurement from the
    // phase's own first frame rather than easing ahead of one and then being
    // overtaken by it (`boot-progress.ts`'s `EASE_CAP_MEASURED`).
    onProgress?.(0, total);
    let done = 0;
    // The third argument is what keeps a chunked pass identical to a whole one:
    // three builds the render list and every material cache key against
    // `targetScene`, so the pipelines a chunk produces are the pipelines it
    // would have produced inside the single call this replaced.
    const compile = async (chunk: THREE.Object3D, weight: number): Promise<void> => {
      await renderer.compileAsync(chunk, camera, scene);
      // `+=` on a shared counter is safe under either mode for the reason it
      // has to be: this is one JavaScript thread, and a chunk's continuation
      // runs to its next `await` without interleaving. Out-of-order resolution
      // is why the counter is a sum of weights rather than an index.
      done += weight;
      onProgress?.(done, total);
    };
    const work = chunks.map((chunk, index) => () => compile(chunk, weights[index] ?? 0));
    // One bounded pool covers both modes, `serial` being the pool of one. Each
    // worker takes the next index and awaits its chunk, so at most `width`
    // compiles are ever in flight and the event loop gets a turn between them.
    const width = Math.min(mode === "serial" ? 1 : PRECOMPILE_IN_FLIGHT, work.length);
    let next = 0;
    await Promise.all(
      Array.from({ length: width }, async () => {
        for (let index = next++; index < work.length; index = next++) await work[index]?.();
      }),
    );
  } finally {
    restoreSceneFlags(saved);
  }
}
